/* eslint-disable max-lines */
/* eslint-disable no-console */
import { isSameContext, isSamePrototype } from '@atlas/atlas-convert-ids';
import { DefaultIndices } from '@pvw/plmvisweb';
import { debounce } from 'lodash';
import Context from './Context';
import {
    DISPLAY_DELAY,
    SELECTION_COLORS,
    SELECTION_REASONS,
    displaySelectedInContext,
    getAllowSelectionOfHiddenWireframe,
    getObjectTypeByPsId,
    getPartContextCache,
    getSelectedObjectsInactiveContexts,
    isAssembly,
    isCurveFromPsId,
    isPointFromPsId,
    processSelectionScope,
    resetPointCache,
    setCurveHighlightStyles,
    setMaterialByPsIds,
    setMouseOnCanvas,
    setPrimarySelectionColor,
    getMouseHoverDisplayDelay,
    getPriorityDistance
} from './atlasSelectionUtils';
import { clearMarkersOfRecType, displayRectangle } from './markerUtils';
import {
    addToContextList,
    clearSecondarySelectionDisplay,
    displaySecondarySelection,
    setSecondarySelectionMaterial
} from './secondarySelectionColor';
const _ = { debounce: debounce };

/**
 * @typedef {object} MouseLoc
 * @property {number} x
 * @property {number} y
 */
/** @typedef {import('./core-elements/BasicElements').SelectionObject} SelectionObject */
/**
 * @typedef {object} ValidatedCandidate
 * @property {SelectionObject} sourceCandidate
 * @property {SelectionObject[]} addCandidates
 * @property {SelectionObject[]} removeCandidates
 */
/**
 * @typedef {object} AcceptedCand
 * @property {ValidatedCandidate} [validated]
 * @property {number} acceptedIndex
 * @property {Set<import('./core-elements/BasicElements').SelectionObject>} candidateSet
 * @property {boolean} deselection
 * @property {boolean} autoDesel
 */
/** @typedef {function(SelectionEvent):void} SelectionCallback */
/** @typedef {function(HighlightEvent):void} HighlightCallback */

/** @enum {string} */
const SELECTION_EVENT_TYPE = {
    PRESELECTION: 'preselection',
    SELECTION: 'selection',
    HIGHLIGHT: 'highlight'
};

class EventEmitter extends EventTarget {
    constructor() {
        super();
    }
}

class SelectionEvent extends Event {
    /**
     *
     * @param {SELECTION_EVENT_TYPE} type
     * @param {import('./core-elements/BasicElements').SelectionObject[]} addCandidates
     * @param {import('./core-elements/BasicElements').SelectionObject[]} removeCandidates
     */
    constructor(type, addCandidates, removeCandidates) {
        super(type);
        /** @type {SelectionObject[]} */
        this.addCandidates = addCandidates;
        /** @type {SelectionObject[]} */
        this.removeCandidates = removeCandidates;
        /** @type {boolean} */
        this.accepted = true;
        /**
         * Callback for async processing selection
         *
         * @type {function(SelectionEvent):Promise}
         */
        this.processEventAsync = undefined;
    }
}

class HighlightEvent extends Event {
    /**
     *
     * @param {SELECTION_EVENT_TYPE} type
     * @param {SelectionObject[]} selectionObjects
     * @param {boolean} highlight
     * @param {boolean} clearAllHighlighted
     */
    constructor(type, selectionObjects, highlight, clearAllHighlighted) {
        super(type);
        /**
         * Objects to hightlight/unhighlight
         *
         * @type {SelectionObject[]}
         */
        this.selectionObjects = selectionObjects;
        /**
         * Is highlight or not
         *
         * @type {boolean}
         */
        this.highlight = highlight;
        /**
         * @type {boolean}
         */
        this.clearAllHighlighted = clearAllHighlighted;
    }
}

/**
 * @class
 * @param {object} viewerIn - An instance of PLMVisWeb viewer
 * @param {object} PLMVisWeb
 */
function AtlasSelection(viewerIn, PLMVisWeb) {
    let viewer = viewerIn;
    // Don't use SVG for selection as performance gets bad (SVG is regenerated on every frame rotating
    // the model for example), set properties of edge/curve stuff to onTop so
    // behavior is the same.  There are also DefaultIndices.LineHighlightMat etc, so for
    // area select we could use that (creating svg bogs down if many edges are getting highlighted,
    // as might be the case in area select)
    let mp = viewer.materialPool;
    mp.updateMaterial(DefaultIndices.LineSelectionMat, { onTop: true });
    mp.updateMaterial(DefaultIndices.WideLineSelectionMat, { onTop: true });
    // Need to do this for highlight as well. SVG highlight cannot handle section - the entire edges
    // are highlighted using svg even after the edges are "trimmed" by section
    mp.updateMaterial(DefaultIndices.LineHighlightMat, { onTop: true });
    mp.updateMaterial(DefaultIndices.WideLineHighlightMat, { onTop: true });

    /** @type {Context[]} */
    let contextStack = [];
    let emitter = new EventEmitter();
    /**
     * @type {AcceptedCand}
     */
    let acceptedCandidates = null;
    /** @type {boolean} */
    let exclusiveGlobalSelection = false;
    // This flag is only set/reset for ESC key and used only when ESC key is down. It is no op w.r.t. any other key.
    // When pressing ESC to dismiss QP dialog, PD asks not to deselect the selected objects. For the rest of the cases,
    // pressing ESC dismisses the selected objects. This is consistent with NX.
    // The client caller sets this flag in QP when ESC key is pressed and resets this flag when QP exits.
    /** @type {boolean} */
    let _escKeepSelected = false;

    let pmiMgr = viewer.pmi;

    let _cameraChange = false;
    const onCameraChanged = (newCamInfo, action) => {
        // whenever the camera is being moved for any reason, disable ourselves
        // until camera change is completed.  Before we used isDragging, which is
        // working for most part but snap point was drawing its previews because
        // it's still getting mouseMove callbacks.  By monitoring camera change
        // we can ensure all display gets cleaned up properly.
        _cameraChange = action !== PLMVisWeb.CameraChange.POST;
    };
    /**
     * given a candidate, send event and return result if accepted
     *
     * @param {SelectionObject[]} candArray
     * @param {boolean} deselection
     * @param {boolean} autoDesel
     * @returns {Promise<SelectionEvent>}
     */
    const validateCandidate = (candArray, deselection, autoDesel) => {
        let context = this.curContext();
        if (!context) {
            return null;
        }
        let curSel = Array.from(context.getSelected());

        let selCand = deselection ? undefined : candArray;
        let deselCand = deselection ? candArray : autoDesel && curSel.length > 0 ? curSel : undefined;
        return this.processPreSelection(selCand, deselCand);
    };
    /**
     * two scenarios this is used for
     * 1) normal selection, addHidden is false
     * 2) cycling selection, where we're looking for next possible valid candidate, typically addHidden false here too
     *
     * @param {AcceptedCand} acceptedCand
     * @param {boolean} deselection
     * @param {boolean} autoDesel
     * @param {boolean} [addHidden=false]
     * @returns {Promise<AcceptedCand>}
     */
    const validateNext = async (acceptedCand, deselection, autoDesel, addHidden = false) => {
        // PR10814048: If RMB during fence selection, acceptedCand.candidateSet is undefined. return undefined in this case
        if (acceptedCand.candidateSet === undefined) {
            return undefined;
        }
        let idx = 0;
        /** @type {{validatedIdx:number, cand:Promise<ValidatedCandidate>}[]} */
        let candArray = [];
        for (let cand of acceptedCand.candidateSet) {
            if (cand.isBlocked && !addHidden) {
                // this and all further candidates will be invalid, so jump to end
                idx = acceptedCand.candidateSet.size;
                continue;
            }
            if (idx <= acceptedCand.acceptedIndex) {
                idx++;
                continue;
            }
            let validatedCand = validateCandidate([cand], deselection, autoDesel).then((res) =>
                res.accepted
                    ? {
                          sourceCandidate: cand,
                          addCandidates: res.addCandidates,
                          removeCandidates: res.removeCandidates
                      }
                    : undefined
            );
            candArray.push({ validatedIdx: idx, cand: validatedCand });
        }

        idx = 0;
        for (let validatedCand of candArray) {
            // eslint-disable-next-line no-await-in-loop
            acceptedCand.validated = await validatedCand?.cand;
            if (acceptedCand.validated) {
                acceptedCand.acceptedIndex = validatedCand.validatedIdx;
                break;
            }
            idx++;
        }

        if (idx >= candArray.length) {
            idx = 0;
            //we got to end of loop w/o another candidate.  Loop around to beginning of list
            for (let cand of acceptedCand.candidateSet) {
                if (idx > acceptedCand.acceptedIndex) {
                    acceptedCand.acceptedIndex = -1; //cant find any valid candidate
                    break;
                }
                if (cand.isBlocked && !addHidden) {
                    // this and all further candidates will be invalid
                    acceptedCand.acceptedIndex = -1; //cant find any valid candidate
                    continue;
                }
                idx++;
            }
        }
        return acceptedCand;
    };

    /**
     * hasHighlightedPsIds returns true if there is any plmvisweb highlighted psIds. The selection pickingMode is set
     * to PLMVisWeb.PickingMode.FREE.
     *
     * @param {object} viewer
     * @returns {boolean} true if there is any highlighted psIds; false otherwise.
     */
    const hasHighlightedPsIds = (viewer) => {
        const highlightedObjs = viewer.getHighlightedPsIds([PLMVisWeb.PickingMode.FREE]);
        // get all the possible plmvisweb returned objects and see if there are any highlighte
        const hasHighlighted = highlightedObjs && Object.values(highlightedObjs).some((val) => val.length);
        return hasHighlighted;
    };

    /**
     * Highlights the objects represented by the input psIds. It also displays edges on top if any.
     *
     * @param {object} viewer - An instance of PLMVisWeb viewer manager object
     * @param {string[]} allPsIds - Array of object psIds to be highlighted. The array could include dimension ids
     * @param {boolean} highlight - true to highlight and false to dehighlight
     * @param {boolean} allowedToUnhighlightAll - if true this function will unhighlight all when appropriate.
     */
    const highlightByPsIds = (viewer, allPsIds, highlight, allowedToUnhighlightAll = true) => {
        const validPsIds = allPsIds && Array.isArray(allPsIds) && allPsIds.length > 0;
        if (!validPsIds) {
            if (!highlight && allowedToUnhighlightAll) {
                if (hasHighlightedPsIds(viewer)) {
                    viewer.clearHighlight();
                }
            }
            return;
        }

        let combinedPsIdSet = new Set();
        allPsIds?.forEach((psId) => combinedPsIdSet.add(psId));

        // Get edges from the faces in allPsIds. NX highlights these edges when faces are highlighted. We'll do the same
        const facePsIds = allPsIds.filter((id) => {
            return getObjectTypeByPsId(viewer, id) === 'Face';
        });
        for (const facePsId of facePsIds) {
            // Get edges of the facePsId
            let fePsIds = viewer.getEdgeIdsById(facePsId);
            fePsIds.forEach((/** @type {string} */ psId) => combinedPsIdSet.add(psId));
        }
        const combinedPsIds = Array.from(combinedPsIdSet);

        if (highlight) {
            // Some curves could have different display styles - solid, dashed, etc. The highlight should display the same styles.
            // Furthermore, the same curve could have different display styles - at some time with the solid style and some other
            // time, the dashed style, etc. Make sure to set the "dashed" property based on the curve's property.
            setCurveHighlightStyles(viewer, combinedPsIds);
            viewer.setHighlightByPsId(combinedPsIds, true, true); //third param { clearFirst: true } - can get rid if first call
        } else if (allowedToUnhighlightAll) {
            if (hasHighlightedPsIds(viewer)) {
                viewer.clearHighlight();
            }
        }
    };

    /**
     * Highlights the selection object represented by the input psIds.
     *
     * @param {object} viewer - An instance of PLMVisWeb viewer manager object
     * @param {import('./core-elements/BasicElements').SelectionObject[]} selectionObjects - array of the selection objects
     * @param {boolean} highlight - true to highlight and false to dehighlight
     * @param {boolean} [allowedToUnhighlightAll=true] - if true this function will unhighlight all when appropriate.
     */
    this.highlightBySelectionObjects = (viewer, selectionObjects, highlight, allowedToUnhighlightAll = true) => {
        const validSelObjs = selectionObjects && Array.isArray(selectionObjects) && selectionObjects.length > 0;
        if (!validSelObjs) {
            if (!highlight && allowedToUnhighlightAll) {
                if (hasHighlightedPsIds(viewer)) {
                    viewer.setHighlightByPsId([], false);
                }
            }
            return;
        }
        let allPsIdSet = new Set();
        let allPsIds = [];
        for (const selObj of selectionObjects) {
            const sourceElem = selObj.sourceElement;
            const psIds = sourceElem.highlight(selObj);
            if (psIds) {
                psIds.forEach((psId) => allPsIdSet.add(psId));
            }
        }
        allPsIds = Array.from(allPsIdSet);
        highlightByPsIds(viewer, allPsIds, highlight, allowedToUnhighlightAll);
    };

    /**
     *
     * @param {AcceptedCand} accCands - Accepted candidated to highlight/unhightlight
     * @param {boolean} highlight - highlight or not
     * @param {boolean} [allowedToUnhighlightAll=true]
     * @param {boolean} [emitEvent=true]
     */
    const highlightCandidates = (accCands, highlight, allowedToUnhighlightAll = true, emitEvent = true) => {
        let cands = [];
        if (accCands && accCands.validated) {
            cands = accCands.deselection ? accCands.validated.removeCandidates : accCands.validated.addCandidates;
        }
        if (emitEvent) {
            let selEvent = new HighlightEvent(SELECTION_EVENT_TYPE.HIGHLIGHT, cands, highlight, !highlight && allowedToUnhighlightAll);
            emitter.dispatchEvent(selEvent);
        }
        this.highlightBySelectionObjects(viewer, cands, highlight, allowedToUnhighlightAll);
    };

    const setBlocking = (psObjects) => {
        let isBlocked = false;
        for (let obj of psObjects) {
            let psId = obj.id;
            let entityType = getObjectTypeByPsId(viewer, psId);

            // block only wireframe edges/curves when it is blocked by a face.
            let isWireFrameEntity = entityType === 'Edge' || isCurveFromPsId(viewer, psId);
            obj.isBlocked = isWireFrameEntity ? (getAllowSelectionOfHiddenWireframe() ? false : isBlocked) : false;
            if (!isBlocked && isBlocking(false, psId)) {
                isBlocked = true;
            }
        }
    };

    const isBlocking = (addHidden, psId) => {
        if (addHidden) {
            return false;
        }
        let curType = getObjectTypeByPsId(viewer, psId);
        let material = undefined;
        if (curType === 'Face') {
            material = viewer.getMaterialByPsId(psId);
        }
        return material && material[0] && material[0].opacity > 0.999;
    };

    /**
     * given x,y, get all candidates from context.  returns set.  List is ordered by closest to front,
     * filter element order
     *
     * @param {Array} psObjects - objects query from PLMVisWeb
     * @param {Context} context
     * @param {boolean} deselection - is deselection
     * @returns {Promise<[Set<import('./core-elements/BasicElements').SelectionObject>, boolean]>}
     */
    const findAllCandidates = async (psObjects, context, deselection) => {
        //loop over these objects till I get to first non edge.  I can't test anything
        //beyond that, or I'll start picking up hidden objects
        let cancelAccepted = false;
        /** @type {Promise<import('./core-elements/BasicElements').SelectionObject[]>[]} */
        let objPromiseArr = [];
        if (psObjects && psObjects.length > 0) {
            // !!!
            // Be cautious with your code here, make sure not to accidentally alter the original order of candidates!
            // !!!
            for (let obj of psObjects) {
                let psId = obj.id;
                objPromiseArr.push(
                    context.findObjects(psId, deselection).then((psIdCandidates) => {
                        for (let cand of psIdCandidates) {
                            if (!cancelAccepted && cand.cancelAccepted) {
                                cancelAccepted = true;
                            }
                            cand.modelPoint = obj.modelPoint;
                            cand.isBlocked = obj.isBlocked;
                        }
                        return psIdCandidates;
                    })
                );
            }
        }
        // It's safe to alter order after Promise.all().
        let candidateArrays = await Promise.all(objPromiseArr);
        let candidates = candidateArrays.flat();
        // PD want the component type has higher priority
        candidates = favorComponentType(candidates);
        return [new Set(candidates), cancelAccepted];
    };

    const snapPointTypes = ['SnapPoint', 'SnapOnCurve', 'SnapOnFace'];
    /**
     * Some times, the id in cand.id is not an atlas id. For example, snap points don't have an atlas id;
     * feature id in cand.id is already stringified. For snap points, We'll use the atlas id of the parent edge;
     * for feature, we'll do JSON.parse to get the atlas id. These atlas ids will be used to check if the objects
     * are in the work part.
     *
     * @param {import('./core-elements/BasicElements').SelectionObject} cand - candidate selection object
     * @returns {Array} atlasId1 - a valid atlasId
     */
    function getAtlasId(cand) {
        let atlasId = undefined;
        if (snapPointTypes.includes(cand.type)) {
            atlasId = cand?.result?.parentId;
        } else if (cand.type === 'Feature') {
            atlasId = JSON.parse(cand.id);
        } else {
            atlasId = cand.id;
        }
        return atlasId;
    }

    /**
     * isInWorkComponent returns true if cand is in the work part/component; false otherwise
     *
     * @param {import('./core-elements/BasicElements').SelectionObject} cand - candidate selection object
     * @returns {boolean} true if cand is in the work part/component; false otherwise
     */
    function isInWorkComponent(cand) {
        if (cand.type === 'Dimension') {
            // This is plmvisweb dimension which doesn't have any context. Always considered it as in the work part
            return true;
        }
        const partContext = getPartContextCache();
        if (!partContext) {
            // This could only happens in unit test if part context is undefined. In all other cases partContext is defined
            return true;
        }
        const atlasId = getAtlasId(cand);
        return (
            (partContext.workComponentAtlasId && isSameContext(partContext.workComponentAtlasId, atlasId)) ||
            isSamePrototype(partContext.workPartAtlasId, atlasId) ||
            isSamePrototype(partContext.displayPartAtlasId, atlasId)
        );
    }

    /**
     * firstCandidateInWorkComponent returns true if the first object in cadidates is in the work part.
     * Otherwise, it returns false.
     *
     * @param {import('./core-elements/BasicElements').SelectionObject[]} candidates - input selection candidates
     * @returns {boolean} true to put candidates in the work part first in the candidates list; false otherwise
     */
    function firstCandidateInWorkComponent(candidates) {
        return candidates?.length > 0 ? isInWorkComponent(candidates[0]) : false;
    }

    /**
     * favorComponentType moves component parts and sub-assembly components to the top of the list if the part/component is
     * hit first. Otherwise if the geometry in the assembly is hit first, the geometry are put on top of the list
     *
     * @param {import('./core-elements/BasicElements').SelectionObject[]} candidates - selection candidate objects
     * @returns {import('./core-elements/BasicElements').SelectionObject[]} - re-ordered selection candidate objects
     */
    function favorComponentType(candidates) {
        /** @type {Map<string, import('./core-elements/BasicElements').SelectionObject>} */
        let compCandidateMap = new Map(); // save part and component candidates for assembly only
        /** @type {Map<string, import('./core-elements/BasicElements').SelectionObject>} */
        let candidatesInWorkComponentMap = new Map(); // save geometry candidates in work component or part
        let isFirstCandidateInWorkComponent = firstCandidateInWorkComponent(candidates);
        /** @type {Map<string, import('./core-elements/BasicElements').SelectionObject>} */
        let otherCandidateMap = new Map(); // save other candidates
        const isAssem = isAssembly(viewer);
        for (let cand of candidates) {
            const atlasId = getAtlasId(cand);
            let id = JSON.stringify(atlasId);
            const isPartOrAssembly = cand.type === 'Part';
            if (isAssem) {
                const inWorkComponent = isInWorkComponent(cand);
                const isWorkComponent = isPartOrAssembly && inWorkComponent;
                if (isWorkComponent) {
                    // Skip work component according to PD's recommendation
                    continue;
                }
                if (isPartOrAssembly && !isWorkComponent) {
                    // save none work components
                    if (!compCandidateMap.has(id)) {
                        compCandidateMap.set(id, cand);
                    }
                } else {
                    // The following enhanced the object order either to be the same as NX or based on distance.
                    // Return objects based on its distance to the cursor is requested by Zel-X. A flag
                    // "priorityDistance" is introduced.
                    // a) getPriorityDistance() === false. We keep the current SWF3D order which is the same as NX:
                    //    1) when geometry/entities in work part are in front of those in components
                    //       geometry/entities in work part
                    //       components
                    //       geometry/entities in components
                    //    2) when geometry/entities in work part are behind those in components
                    //       components
                    //       geometry/entities in work part
                    //       geometry/entities in components
                    // b) getPriorityDistance() === true. Zel-X requires the entities are returned based on it distance to cursor
                    //    1) when geometry/entities in work part are in front of those in components
                    //       geometry/entities in work part
                    //       components
                    //       geometry/entities in components
                    //    2) when geometry/entities in work part are behind those in components
                    //       components
                    //       geometry/entities (distance based)
                    if (!getPriorityDistance() || (getPriorityDistance() && isFirstCandidateInWorkComponent)) {
                        if (inWorkComponent) {
                            if (!candidatesInWorkComponentMap.has(id)) {
                                // save geometry and any entities that are directly in work component
                                candidatesInWorkComponentMap.set(id, cand);
                            }
                            // If it gets to here without going through the above set, it means we get a duplicated id or
                            // selection object. We need to skip it.
                            continue;
                        }
                    }
                    // save geometry not in the above case
                    if (!otherCandidateMap.has(id)) {
                        // save geometry and any entities that are not in work component
                        otherCandidateMap.set(id, cand);
                    }
                }
            } else {
                if (isPartOrAssembly) {
                    // Skip part according to PD's recommendation
                    continue;
                }
                if (!otherCandidateMap.has(id)) {
                    // save geometry and any entities in piece part
                    otherCandidateMap.set(id, cand);
                }
            }
        }
        // Merge compCandiateMap and otherCandidateMap
        if (isFirstCandidateInWorkComponent) {
            return [...candidatesInWorkComponentMap.values(), ...compCandidateMap.values(), ...otherCandidateMap.values()];
        }
        return [...compCandidateMap.values(), ...candidatesInWorkComponentMap.values(), ...otherCandidateMap.values()];
    }

    /**
     *
     * @param {Array} psObjects - objects query from PLMVisWeb
     * @param {Context} context
     * @param {boolean} [deselection]
     * @param {boolean} [autoDesel]
     * @param {boolean} [addHidden]
     * @returns {Promise<[AcceptedCand, boolean]>}
     */
    const findAcceptedCandidates = async (psObjects, context, deselection = false, autoDesel = true, addHidden = false) => {
        let [candSet, cancelAccepted] = await findAllCandidates(psObjects, context, deselection);
        if (
            acceptedCandidates &&
            acceptedCandidates.candidateSet &&
            acceptedCandidates.candidateSet.size > 0 &&
            !cancelAccepted &&
            candSet.size > 0
        ) {
            const [currCandsFirstElem] = acceptedCandidates.candidateSet; // get the fisrt element in the current candidate set
            const [candsFirstElem] = candSet; // get the fisrt element in new candidate set
            if (JSON.stringify(currCandsFirstElem.id) !== JSON.stringify(candsFirstElem.id)) {
                cancelAccepted = true;
            }
        }
        if (candSet && candSet.size > 0) {
            /** @type {AcceptedCand} */
            let acceptedCand = {
                acceptedIndex: -1,
                candidateSet: candSet,
                deselection: deselection,
                autoDesel: autoDesel
            };
            acceptedCand = await validateNext(acceptedCand, deselection, autoDesel, addHidden);

            if (acceptedCand.validated) {
                return [acceptedCand, false];
            }
        }
        return [undefined, undefined];
    };

    const favorDimensions = (curObjects) => {
        let dimIdx = 0;
        let dimObjs = [];
        let nonDimObjs = [];
        for (const obj of curObjects) {
            let curType = getObjectTypeByPsId(viewer, obj.id);
            if (curType === 'Dimension') {
                dimObjs.push(obj);
            } else {
                nonDimObjs.push(obj);
            }
        }
        return dimObjs.concat(nonDimObjs);
    };

    const closest = (el, classSelector) => {
        let parent = el;
        while (parent && parent !== document.body) {
            if (parent.matches && parent.matches(classSelector)) {
                return parent;
            }
            parent = parent.parentNode;
        }
    };

    const getObjectIdsInArea = async (locMouseX, locMouseY, offsetX, offsetY, partialEnclosed, pickMask, visibleOnly) => {
        let promise = new Promise((resolve, reject) => {
            try {
                viewer.getObjectIdsInArea(
                    locMouseX,
                    locMouseY,
                    offsetX,
                    offsetY,
                    (psIds) => {
                        // results is array of psId strings
                        resolve(psIds);
                    },
                    { partial: partialEnclosed, pickingMode: pickMask, visibleOnly: visibleOnly }
                );
            } catch (error) {
                reject(error);
            }
        });
        return promise;
    };

    const delayAreaHighlight = _.debounce(highlightCandidates, DISPLAY_DELAY.AREA);

    /**
     *
     * @param {Context} context
     * @param {MouseLoc} locMouse
     * @param {MouseEvent} e
     */
    const selectObjectsInArea = async (context, locMouse, e) => {
        let pickMask = PLMVisWeb.PickingMode.FACE | context.getPickingMask(true);
        let partial = e.offsetX - locMouse.x < 0;
        let psIds = await getObjectIdsInArea(locMouse.x, locMouse.y, e.offsetX, e.offsetY, partial, pickMask, true /*visibleOnly*/);
        if (psIds !== undefined && psIds.length > 0) {
            let objPromiseArr = [];
            for (let element of context.getElements()) {
                if (element.active) {
                    objPromiseArr.push(element.objectsInArea(psIds, partial));
                }
            }
            await Promise.all(objPromiseArr).then(async (resolvedObjectsArray) => {
                let objArr = [];
                for (let i = 0; i < resolvedObjectsArray.length; i++) {
                    objArr.push(...resolvedObjectsArray[i]);
                }
                objPromiseArr = [];
                let curSel = Array.from(context.getSelected());
                let autoDesel = !context.getMultiSelect() || (exclusiveGlobalSelection && !e.ctrlKey && !e.shiftKey);
                let selectedCand = [];
                let unselectedCand = [];
                // For Fence/Area Exclusive selection
                // Unmodified MB1/LMB fence drag
                // a) The current selection is cleared.
                // b) Items that satisfy the fence operation are selected.
                // Modified MB1/LMB + Shift fence drag
                // a) Currently selected items remain in the select set.
                // b) Items that satisfy the fence operation are added to the select set.
                // Modified MB1/LMB + Ctrl fence drag
                // a) Only currently selected items considered.
                // b) Items that satisfy the fence operation are removed from the selection.
                // c) If there are no items in the select set, then the operation performs no action.
                for (let obj of objArr) {
                    if (context.isObjSelected(obj)) {
                        selectedCand.push(obj);
                    } else {
                        unselectedCand.push(obj);
                    }
                }
                let isDesel = exclusiveGlobalSelection ? e.ctrlKey && !e.shiftKey : e.shiftKey;
                // In general, only unselected objects can be candidates to be selected, but for auto deselect,
                // already selected will be deselected automatically so all objects in area should to be selected
                let selCand = isDesel ? undefined : autoDesel ? objArr : unselectedCand;
                let deselCand = [];
                if (isDesel) {
                    // only already selected objects can be deselected
                    deselCand = selectedCand;
                } else {
                    if (autoDesel) {
                        // only already selected and not in current selection area objects can be deselected,
                        // keep the selected objects in area as to be selected.
                        let selctedId = selectedCand.map((cand) => JSON.stringify(cand.id));
                        deselCand = curSel.filter((selObj) => {
                            let selObjId = JSON.stringify(selObj.id);
                            return selctedId.find((candId) => candId === selObjId) === undefined;
                        });
                    } else {
                        deselCand = undefined;
                    }
                }
                let selEvent = await this.processPreSelection(selCand, deselCand);
                if (selEvent.accepted === true) {
                    acceptedCandidates = Object.assign({
                        // For now lets put the first element in the array as sourceCandidate.
                        // The sourceCandidate is not going to be used during area selection.
                        validated: { sourceCandidate: objArr[0], addCandidates: selEvent.addCandidates, removeCandidates: selEvent.removeCandidates },
                        deselection: isDesel
                    });
                    delayAreaHighlight.cancel();
                    delayAreaHighlight(acceptedCandidates, true, true);
                }
            });
        } else {
            delayAreaHighlight.cancel();
            highlightCandidates(acceptedCandidates, false, true);
            acceptedCandidates = null;
        }
    };
    const delaySelectObjectsInArea = _.debounce(selectObjectsInArea, DISPLAY_DELAY.AREA);

    const isPreviewBody = (viewer, obj) => {
        const properties = viewer.properties;
        let bodyPsIds = undefined;
        if ((obj && obj.type === 'edge') || obj.type === 'face') {
            bodyPsIds = viewer.getBodyIdsById(obj.id);
        } else if (obj.type === 'body') {
            bodyPsIds = [obj.id];
        }
        let found = false;
        if (bodyPsIds && bodyPsIds.length > 0) {
            const geomProperty = properties.getProperties(bodyPsIds[0]);
            if (geomProperty.properties && geomProperty.properties.PreviewEntity) {
                found = true;
            }
        }
        return found;
    };

    // removes the geometry on the preview body
    const removePreviewGeometry = (viewer, inObjects) => {
        return inObjects.filter((el) => {
            return !isPreviewBody(viewer, el);
        });
    };

    /**
     * if "allow selection of hidden wireframe" is set, move the first edge or curve found in curObjects array to the
     * beginning of the curObjects
     *
     * @param {any[]} curObjects
     */
    const resequence = (curObjects) => {
        if (getAllowSelectionOfHiddenWireframe()) {
            const idx = curObjects.findIndex((obj) => obj.type === 'edge' || isPointFromPsId(viewer, obj.id) || isCurveFromPsId(viewer, obj.id)); // finds index of the first edge or curve in curObjects
            if (idx > 0) {
                // idx === 0 is already at the beginning of the array
                const firstEdge = curObjects.splice(idx, 1)[0];
                curObjects.unshift(firstEdge);
            }
        }
    };

    /**
     *
     * @param {AcceptedCand} prevAcceptedCandidates
     * @param {AcceptedCand} acceptedCandidates
     * @returns {boolean}
     */
    const isSameCandidates = (prevAcceptedCandidates, acceptedCandidates) => {
        let cands = acceptedCandidates?.validated?.addCandidates;
        let prevCands = undefined;
        if (Array.isArray(cands) && cands.length > 0) {
            prevCands = prevAcceptedCandidates?.validated?.addCandidates;
            if (!Array.isArray(prevCands) || prevCands.length !== cands.length) {
                return false;
            }
        } else {
            cands = acceptedCandidates?.validated?.removeCandidates;
            if (Array.isArray(cands) && cands.length > 0) {
                prevCands = prevAcceptedCandidates?.validated?.removeCandidates;
                if (!Array.isArray(prevCands) || prevCands.length !== cands.length) {
                    return false;
                }
            }
        }
        if (!Array.isArray(cands) || !Array.isArray(prevCands)) {
            return false;
        }
        for (let i = 0; i < prevCands.length; i++) {
            if (JSON.stringify(prevCands[i].id) !== JSON.stringify(cands[i].id)) {
                return false;
            }
        }
        return true;
    };

    const filterOutObjectsInHiddenBody = (curObjects) => {
        // We turned on allowing hidden objects by Zel-X's request. This is to enable the selection of the invisible edges in
        // shaded mode. There is a case where the whole body is hidden on purpose. In this case, we should not allow the selection
        // of any edges, faces, etc. in the totally hidden body. Because of this, we need to filter out edges, faces, bodies in the
        // hidden (invisible) body.
        if (curObjects?.length > 0) {
            curObjects = curObjects.filter((curObj) => {
                const bodies = viewer.getBodyIdsById(curObj.id);
                // If curObj is any of edge, face, or body, return it only when it is on the visible body.
                // Otherwise, just return the object
                return bodies && Array.isArray(bodies) && bodies.length > 0 ? viewer.getVisibilityStateByPsId(bodies[0]) : true;
            });
        }
        return curObjects;
    };

    let _cameraRegistered = false;
    /**
     *
     * @param {Context} context
     * @param {MouseEvent} e
     * @param {boolean} highlight
     * @param {number} [x]
     * @param {number} [y]
     */
    const validateObjectsUnderCursor = async (context, e, highlight, x = e.offsetX, y = e.offsetY) => {
        // always add faces regardless of what the elements want.  This allows isBlocking to
        // work even if faces aren't needed (as we need to know about what faces are in front)
        let pickMask = PLMVisWeb.PickingMode.FACE | context.getPickingMask();
        let params = { pickingMode: pickMask, noDuplicates: true, visibleOnly: false };
        // useDistance shouldn't be used in 1.7.6, but we may have someone using 1.7.5 still
        if (PLMVisWeb.VERSION && PLMVisWeb.VERSION.startsWith && PLMVisWeb.VERSION.startsWith('1.7.5')) {
            params.useDistance = true;
        }
        /** @type {any[]} */
        let curObjects = viewer.getObjectIdsAtViewCoordinate(x, y, params);
        // a weird spot to register a callback.  But AtlasSelection gets initialized very early
        // in the process in swf3d, apparently too early.  Registering early corrupts PLMVisWeb,
        // I get internal errors when PLMVisWeb tries to react to mouse move.  Not sure what's
        // happening.  But I found even onMouseMove gets called very early, before any parts are
        // loaded, and that even putting this at the beginning of it causes the same error.  So
        // I'm waiting until we've gotten something from PLMVisWeb before setting it.
        if (curObjects.length > 0 && !_cameraRegistered) {
            viewer.registerCameraChangedEvent(onCameraChanged, PLMVisWeb.CameraChange.BOTH);
            _cameraRegistered = true;
        }
        curObjects = filterOutObjectsInHiddenBody(curObjects);
        // TEMPORARY FIX - getting bad objects from PLMVisWeb, and ISIM can't wait for a fix from
        // PLMVisWeb.  The following line filters out any bad objects.  It should be removed as soon
        // as possible.  Specific issue is exposed using snap points on wf curves.
        curObjects = curObjects.filter((curObj) => {
            let curType = undefined;
            try {
                curType = getObjectTypeByPsId(viewer, curObj.id);
            } catch {
                curType = undefined;
            }
            return curType && curType.length > 0;
        });

        // Filter out geometry on the preview body
        curObjects = removePreviewGeometry(viewer, curObjects);
        curObjects = favorDimensions(curObjects);
        setBlocking(curObjects);
        // if "allow selection of hidden wireframe" is set, move the first edge or curve to the beginning of the curObjects
        resequence(curObjects);

        for (let element of context.getElements()) {
            if (element.onMouseMove && element.active) {
                // eslint-disable-next-line no-await-in-loop
                let addedObj = await element.onMouseMove(e, curObjects);
                if (addedObj) {
                    curObjects.unshift(addedObj);
                }
            }
        }
        let same = false;
        const singleSelMode = !context.getMultiSelect();
        // We allow only one object in selection when:
        // (1) in a single selection context other than global selection, i.e. a selection box in a command
        // (2) in a exclusive global selection, and the ctrl key is not pressed
        // [acceptedCandidates, same] = await findAcceptedCandidates(curObjects, context, e.shiftKey, singleSelMode || (exclusiveGlobalSelection && !e.ctrlKey));

        // In exclusive mode, press Shift key can also add/remove to selection.
        let prevAcceptedCandidates = acceptedCandidates;
        if (exclusiveGlobalSelection && e.shiftKey) {
            let [acceptedCandidatesTmp, sameTmp] = await findAcceptedCandidates(curObjects, context, true, singleSelMode);
            // FIXME: The global acceptedCandidates may be reassigned based on an outdated value here!!!
            [acceptedCandidates, same] = acceptedCandidatesTmp
                ? [acceptedCandidatesTmp, sameTmp]
                : await findAcceptedCandidates(curObjects, context, false, singleSelMode);
        } else {
            [acceptedCandidates, same] = await findAcceptedCandidates(curObjects, context, e.shiftKey, singleSelMode);
        }
        if (highlight) {
            if (acceptedCandidates) {
                // Do we update display or not?  Some Zel issues have shown that
                // same flag from findAcceptedCandidates can't be trusted - and that
                // flag was specifically regarding whether revalidation should occur.
                // Here, I'm comparing at the very end whether anything has changed.  If
                // so, update display.  This is important now because of the delayed highlighting,
                // without this check Zel gets flickering over bodies.
                same = isSameCandidates(prevAcceptedCandidates, acceptedCandidates);
                if (!same) {
                    // clear display immediately, but delay new display.  This allows
                    // us to avoid flashing as the mouse moves across the display, but
                    // still looks responsive when exiting the currently highlighted object
                    highlightCandidates(acceptedCandidates, false, prevAcceptedCandidates !== undefined);
                    highlightData.highlight = true;
                    highlightData.allowedToUnhighlightAll = true;
                    highlightData.emitEvent = true;
                }
            } else {
                highlightCandidates(acceptedCandidates, false, prevAcceptedCandidates !== undefined);
            }
        }
    };

    /**
     *
     * @param startEl
     * @param endEl
     * @returns {MouseLoc}
     */
    function getOffsetParent(startEl, endEl) {
        let offsetLeft = 0;
        let offsetTop = 0;
        do {
            if (!isNaN(startEl.offsetLeft)) {
                offsetLeft += startEl.offsetLeft;
            }
            if (!isNaN(startEl.offsetTop)) {
                offsetTop += startEl.offsetTop;
            }
            startEl = startEl.offsetParent;
        } while (startEl && startEl !== endEl);
        return {
            x: offsetLeft,
            y: offsetTop
        };
    }

    const _dragOverCb = (e) => {
        const context = this.curContext();
        if (context.isDragAndDrop()) {
            // make sure to transform the x, y into canvas local coordinate space for dragover event
            // since the event is registered against window.document.
            const offset = getOffsetParent(viewer.domElement, e.currentTarget);
            const x = e.pageX - offset.x;
            const y = e.pageY - offset.y;
            _evaluateEvent(e, x, y, true);
        }
    };

    let mouseMoveTimer;
    let highlightData = {
        highlight: false,
        allowedToUnhighlightAll: false,
        emitEvent: true
    };
    const mouseStopped = () => {
        // Highlight when mouse hovers over object and stops
        highlightCandidates(acceptedCandidates, highlightData.highlight, highlightData.allowSelectionOfHiddenWireframe, highlightData.emitEvent);
    };

    /**
     *
     * @param {MouseEvent} e
     */
    const _mouseMoveCb = (e) => {
        clearTimeout(mouseMoveTimer);
        // Since we allow programmatcally setting of the mouse hover display delay, we need to move this call into mouseMoveCb,
        // otherwise, the change would not be picked up.
        const timeThreshold = getMouseHoverDisplayDelay();
        mouseMoveTimer = setTimeout(mouseStopped, timeThreshold);
        _evaluateEvent(e, e.offsetX, e.offsetY, false);
    };

    let _isFenceSelection = undefined;
    /**
     * This function only returns the last selection method. It returns true if the last selection is
     * fence selection or false if the last selection is a single selection. This method doesn't have knowledge of any previous selection method.
     *
     * @returns {boolean} - true: last selection is fence selection; false: last selection is a single selection
     */
    this.lastIsFenceSelection = () => {
        return _isFenceSelection;
    };

    const _evaluateEvent = async (e, x, y, isDragOver) => {
        // It's up to the clients to put .swf3d-selection-highlight as low as possible.
        // e.g. Don't put it in a parent that is basically the whole window, it wouldn't make sense,
        // and would mess up this callback.
        if (closest(e.target, '.swf3d-selection-highlight')) {
            return;
        }

        let context = this.curContext();
        if (_dragging && !context.getAllowAreaSelect()) {
            return;
        }

        // Capture where the mouse is for selection scope checking
        if (viewer.input.overCanvas(e)) {
            setMouseOnCanvas(true);
        } else {
            setMouseOnCanvas(false);
        }

        // if UI override the mouseUp event, we'd skip further processing until the override is reset
        if (_override || _cameraChange) {
            return;
        }

        if (!isDragOver) {
            // Starting from PLMVisWeb 1.7.4-31, PLMVisWeb will not stops the propagation of the event in the inputManager.
            // AtlasSelection needs to stop the event propagation in all its listeners after the event is handled.
            e.stopPropagation();
        }

        let locMouse = _mouseDown;
        let resetHighlightAndExit = false;

        if (locMouse) {
            let dist = Math.sqrt(Math.pow(e.offsetX - locMouse.x, 2) + Math.pow(e.offsetY - locMouse.y, 2));
            if (e.buttons === 1 && dist > 5) {
                //drag, not click, must have some button(s) pressed
                _dragging = true;
            } else if (e.buttons !== 0 && dist > 5) {
                resetHighlightAndExit = true;
            }
        }

        if (_dragging && context && !context.getAllowAreaSelect()) {
            resetHighlightAndExit = true;
        }

        if (!isDragOver && !viewer.input.overCanvas(e)) {
            resetHighlightAndExit = true;
        }

        if (resetHighlightAndExit) {
            highlightCandidates(acceptedCandidates, false, acceptedCandidates !== null);
            acceptedCandidates = null;
            clearMarkersOfRecType();
            return;
        }

        if (context) {
            if (_dragging && !_dimPosChanged) {
                _isFenceSelection = true;
                displayRectangle(e, locMouse);
                delaySelectObjectsInArea(context, locMouse, e);
            } else {
                _isFenceSelection = false;
                await validateObjectsUnderCursor(context, e, true, x, y);
            }
        }
    };

    /**
     *
     * @param {Context} context
     * @param {SELECTION_REASONS} reason
     */
    const notifyContextElements = (context, reason) => {
        for (let obj of context) {
            if (obj.sourceElement) {
                obj.sourceElement.select(obj, false, reason);
            }
        }
    };

    /**
     *
     * @param {Context} context
     * @param {SELECTION_REASONS} reason
     */
    const deselectDisplay = (context, reason) => {
        notifyContextElements(context, reason);
        viewer.selectNone();
    };

    /**
     * deselect selected objects in the selection context
     *
     * @param {Context} context - selection context
     * @param {boolean} [emitEvent=true] - true to emit the selection event; false otherwise
     */
    this.deselectAll = (context, emitEvent = true) => {
        let selected = Array.from(context.getSelected());
        if (selected.length > 0) {
            // Display shows entities as selected only for the entities in current context (active or primary),
            // so deselectDisplay should not be called for non-current context (inactive or secondary)
            // as it would motify selected state of entities in current context.
            if (context === this.curContext()) {
                //unhighlight everything
                deselectDisplay(context, SELECTION_REASONS.TERMINATE);
            } else {
                // for non active context, notify its context elements so that it can
                // cleanup data such as snap points.
                notifyContextElements(context, SELECTION_REASONS.TERMINATE);
            }
            context.clearSelected();
            if (emitEvent) {
                let selEvent = new SelectionEvent(SELECTION_EVENT_TYPE.SELECTION, undefined, selected);
                emitter.dispatchEvent(selEvent);
            }
        }
    };

    /**
     *
     * @param {Iterable<SelectionObject>} selObjects
     * @param {SELECTION_REASONS} reason
     * @param {boolean} [selected=true]
     */
    const displaySelected = (selObjects, reason, selected = true) => {
        let context = this.curContext();
        displaySelectedInContext(viewer, context, PLMVisWeb, selObjects, reason, selected);
    };

    /**
     *
     * @returns {boolean}
     */
    this.getExclusiveGlobalSelection = () => {
        return exclusiveGlobalSelection;
    };

    /**
     *
     * @param {boolean} v - is exclusive mode
     */
    this.setExclusiveGlobalSelection = (v) => {
        if (v !== exclusiveGlobalSelection) {
            exclusiveGlobalSelection = v;
        }
    };

    this.updateAtlasSelected = (modelId, addAtlasInfo, removeAtlasInfo, emitEvent = true) => {
        let addObjects = [];
        let removeObjects = [];
        if (addAtlasInfo) {
            for (let addAtlas of addAtlasInfo) {
                let selObject = addAtlas.selElement.getSelectionObject(modelId, addAtlas.atlasId);
                addObjects.push(selObject);
            }
        }
        if (removeAtlasInfo) {
            for (let removeAtlas of removeAtlasInfo) {
                let selObject = removeAtlas.selElement.getSelectionObject(modelId, removeAtlas.atlasId);
                removeObjects.push(selObject);
            }
        }

        this.updateSelected(addObjects, removeObjects, emitEvent);
    };

    /**
     *
     * @param {import('./core-elements/BasicElements').SelectionObject[]} addObjects
     * @param {import('./core-elements/BasicElements').SelectionObject[]} removeObjects
     * @param {boolean} [emitEvent]
     */
    this.updateSelected = (addObjects, removeObjects, emitEvent = true) => {
        let context = this.curContext();
        let addThem = addObjects && addObjects.length > 0;
        let removeThem = removeObjects && removeObjects.length > 0;
        if (context) {
            if (addThem) {
                context.addToSelected(addObjects);
                if (removeThem) {
                    // its more efficient to highlight what's been added, or unhighlight
                    // what's been removed.  But when there are both added and removed objects,
                    // there could be overlaps which can get messy.  So in this case clear the
                    // whole display, update the selection list, and rehighlight everything in the
                    // selection list.
                    deselectDisplay(context, SELECTION_REASONS.TERMINATE);
                    context.removeFromSelected(removeObjects);
                    displaySelected(context, SELECTION_REASONS.FOCUS_IN, true);
                } else {
                    displaySelected(addObjects, SELECTION_REASONS.FOCUS_IN, true);
                }
            } else if (removeThem) {
                context.removeFromSelected(removeObjects);
                displaySelected(removeObjects, SELECTION_REASONS.TERMINATE, false);
            }
            if (addThem || removeThem) {
                // Make sure to cancel any pending highlight after selected/unselected are updated
                delayAreaHighlight.cancel();
            }
        }
        if (emitEvent && (addThem || removeThem)) {
            this.dispatchSelectionEvent(SELECTION_EVENT_TYPE.SELECTION, addObjects, removeObjects);
        }
    };

    /**
     * psids to be highlighted may be changed when editing, this method allows client to update highlighted psids.
     *
     * @param {Context} [context] - optional, use current context if it's undefined
     * @param {import('./core-elements/BasicElements').SelectionObject[]} [selectionObjects] - optional, selection objectes newly created or changed
     */
    this.updateSelectionObject = (context, selectionObjects) => {
        const curContext = this.curContext();
        if (!context || context === curContext) {
            // clear highlight
            viewer.selectNone();
            // replace selection object in context as client may create new seleciton object or reuse selection object
            if (Array.isArray(selectionObjects)) {
                curContext.addToSelected(selectionObjects);
            }
            // highlight new
            displaySelected(curContext.getSelected(), SELECTION_REASONS.FOCUS_IN, true);
        } else {
            // an inactive context, update secondary highlight
            // FIXME: if client change psids of selection object in context directly, psids in our cached green objects will also be changed
            // which will cause us don't now correct psids to unhighlight! Client must call in bellow order in this case:
            //   sel.updateSelectionObject(context); // clear current secondary highlight
            //   selObj.psIds = newPsIds;
            //   sel.updateSelectionObject(context, [selObj]); // highlight again
            // If user create new selection object, it's OK to call only once
            //   sel.updateSelectionObject(context, newSelObjs);
            let useSecondaryColor = context.getSecondaryActive();
            if (Array.isArray(selectionObjects)) {
                if (useSecondaryColor) {
                    clearSecondarySelectionDisplay(viewer);
                }
                context.addToSelected(selectionObjects);
                if (useSecondaryColor) {
                    displaySecondarySelection(viewer);
                }
            } else {
                if (useSecondaryColor) {
                    clearSecondarySelectionDisplay(viewer);
                }
            }
        }
    };

    /**
     *
     * @param {Promise} prom
     * @param {number} [time]
     * @param {any} [exception]
     * @returns {Promise}
     */
    this.promiseTimeout = (prom, time = 2000, exception) => {
        let timer;
        return Promise.race([prom, new Promise((_r, rej) => (timer = setTimeout(rej, time, exception)))]).finally(() => clearTimeout(timer));
    };

    /**
     *
     * @param {import('./core-elements/BasicElements').SelectionObject[]} addObjects
     * @param {import('./core-elements/BasicElements').SelectionObject[]} removeObjects
     * @returns {Promise<SelectionEvent>}
     */
    this.processPreSelection = (addObjects, removeObjects) => {
        let selEvent = this.dispatchSelectionEvent(SELECTION_EVENT_TYPE.PRESELECTION, addObjects, removeObjects);
        if (selEvent.processEventAsync) {
            let timeoutErr = Symbol();
            return this.promiseTimeout(selEvent.processEventAsync(selEvent), 2000, timeoutErr)
                .then(() => selEvent)
                .catch((err) => {
                    if (err === timeoutErr) {
                        console.error('Timeout in preselection callback.');
                    } else {
                        console.error('Exception occurred in preselection callback.');
                    }
                    selEvent.accepted = false; // not sure about this - callback could have already set
                    return selEvent; // return the selection event regardless of failure
                });
        }
        return new Promise((resolve, reject) => {
            resolve(selEvent);
        });
    };

    /**
     *
     * @param {SELECTION_EVENT_TYPE} eventType
     * @param {import('./core-elements/BasicElements').SelectionObject[]} addObjects
     * @param {import('./core-elements/BasicElements').SelectionObject[]} removeObjects
     * @returns {SelectionEvent}
     */
    this.dispatchSelectionEvent = (eventType, addObjects, removeObjects) => {
        let rejectAll = false;
        let adjustedAddObjects = addObjects;
        let adjustedRemoveObjects = removeObjects;
        // Filter out all the candidates that are out of the selection scope and keep only the ones in scope
        const selectionScope = this.curContext().getSelectionScope();
        let processedObjects = processSelectionScope(viewer, addObjects, removeObjects, selectionScope);
        adjustedAddObjects = processedObjects.addObjects;
        adjustedRemoveObjects = processedObjects.removeObjects;
        if ((!adjustedAddObjects || adjustedAddObjects.length === 0) && (!adjustedRemoveObjects || adjustedRemoveObjects?.length === 0)) {
            rejectAll = true;
        }

        let selEvent = new SelectionEvent(eventType, adjustedAddObjects, adjustedRemoveObjects);
        if (rejectAll) {
            selEvent.accepted = false;
        } else {
            emitter.dispatchEvent(selEvent);
        }
        return selEvent;
    };

    /**
     * As we move to support multiple selection contexts, the context at hand may not be on the contextStack.
     * setActiveInContext handles setting the element in the context to be active
     *
     * @param {Context} context
     * @param {import('./core-elements/BasicElements').SelectionElement[]} filterElements
     * @param {boolean} active
     * @param {boolean} exclusive
     * @param {boolean} [dragAndDrop=false]
     */
    this.setActiveInContext = (context, filterElements, active, exclusive, dragAndDrop = false) => {
        if (context) {
            if (!active || exclusive) {
                deselectDisplay(context, SELECTION_REASONS.TERMINATE);
            }
            context.setActive(filterElements, active, exclusive, dragAndDrop);
            if (!active || exclusive) {
                displaySelected(context.getSelected(), SELECTION_REASONS.FOCUS_IN, true);
            }
        }
    };

    /**
     * setActive sets the element in the current context to be active
     *
     * @param {import('./core-elements/BasicElements').SelectionElement[]} filterElements
     * @param {boolean} active
     * @param {boolean} exclusive
     * @param {boolean} [dragAndDrop=false]
     */
    this.setActive = (filterElements, active, exclusive, dragAndDrop = false) => {
        let context = this.curContext();
        this.setActiveInContext(context, filterElements, active, exclusive, dragAndDrop);
    };

    //dragging stuff is temporary until we have a mouse module that does this
    /**
     * @type {MouseLoc}
     */
    let _mouseDown = null;
    let _dragging = false;
    let _override = false; // sets up to override mouse event. This is set through UI.
    let _mouseDownInGraphicArea = false;

    /**
     *
     * @param {MouseEvent} e
     */
    const _mouseDownCb = (e) => {
        _mouseDownInGraphicArea = true; // This callback is reached only when mouse down is in the graphics area
        _mouseDown = { x: e.offsetX, y: e.offsetY };

        // Starting from PLMVisWeb 1.7.4-31, PLMVisWeb will not stops the propagation of the event in the inputManager.
        // AtlasSelection needs to stop the event propagation in all its listeners after the event is handled.
        e.stopPropagation();
    };

    /**
     * Compute the distance square between selection object to pick point.
     *
     * @param {import('./core-elements/BasicElements').SelectionObject} selObj - selection object
     * @param {object} pickPoint - 3D pick point location { x: valueX, y: valueY, z: valueZ }
     * @returns {object} distance square in { isSnapPoint: true/false, dist2: value }
     */
    this.selObjToPickPointDistSqr = (selObj, pickPoint) => {
        let dist2 = undefined;
        if (snapPointTypes.includes(selObj.type)) {
            if (selObj?.result.type === 'Center' || selObj?.result.type === 'Pole') {
                // Since snap point is not a plmvisweb object, the pick point is on some geometry objects
                // which is not on the center or pole snap point. We need to compare the screen points
                // instead of the actual points
                const viewCoords = viewer.projectModelPointToViewCoordinate(pickPoint.x, pickPoint.y, pickPoint.z);
                dist2 = Math.pow(selObj?.result.xPx - viewCoords[0], 2) + Math.pow(selObj?.result.yPx - viewCoords[1], 2);
            } else {
                dist2 =
                    Math.pow(selObj?.result.location.x - pickPoint.x, 2) +
                    Math.pow(selObj?.result.location.y - pickPoint.y, 2) +
                    Math.pow(selObj?.result.location.z - pickPoint.z, 2);
            }
            return { isSnapPoint: true, dist2: dist2 };
        }
        dist2 =
            Math.pow(selObj?.modelPoint[0] - pickPoint.x, 2) +
            Math.pow(selObj?.modelPoint[1] - pickPoint.y, 2) +
            Math.pow(selObj?.modelPoint[2] - pickPoint.z, 2);
        return { isSnapPoint: false, dist2: dist2 };
    };

    /**
     * findSelectedNearPickPoint returns the first selected object near the pickPoint.
     * If any snap point on edges, curves or faces is on the pickPoint, it'll be returned over the edge,
     * curve, face, body, etc. it is on.
     *
     * @param {object[]} curObjects - array of objects returned from viewer.getObjectIdsAtViewCoordinate.
     *                                These objects have the same modelPoint
     * @param {object[]} selected - the selected object in the selection context
     * @returns {object} - the first selected object coincides with the one under the cursor. Snap Point
     *                     has the priority over geometry
     */
    const findSelectedNearPickPoint = (curObjects, selected) => {
        const validData = curObjects && curObjects.length > 0 && selected && selected.length > 0;
        if (!validData) {
            return [];
        }

        let minDistance2 = 1e10;
        let snapPointObj = undefined;
        const distanceTolerance = 5; // We use 5 pixels to judge if mouse drags.
        const distanceTolerance2 = distanceTolerance * distanceTolerance;
        // We use half of that to see if mouse is on top of a snap point
        const pickPoint = { x: curObjects[0].modelPoint[0], y: curObjects[0].modelPoint[1], z: curObjects[0].modelPoint[2] };
        // find the closest snap point which is "on" the pick point
        for (const selObj of selected) {
            let distResult = this.selObjToPickPointDistSqr(selObj, pickPoint);
            if (distResult.isSnapPoint) {
                if (distResult.dist2 <= distanceTolerance2 && distResult.dist2 < minDistance2) {
                    snapPointObj = selObj;
                    minDistance2 = distResult.dist2;
                }
            }
        }
        let otherObj = undefined;
        if (!snapPointObj) {
            for (const curObj of curObjects) {
                for (const selObj of selected) {
                    if (curObj.id === selObj.psIds[0]) {
                        otherObj = selObj;
                        break;
                    }
                }
                if (otherObj) {
                    break;
                }
            }
        }
        return snapPointObj ? [snapPointObj] : otherObj ? [otherObj] : [];
    };

    /**
     *
     * @param {MouseEvent} e
     */
    const _mouseUpCb = async (e) => {
        let wasDragging = _dragging;
        _dragging = false;

        // set locMouse and _mouseDown first, otherwise if we hit the return below we wind up with
        // a stale _mouseDown which can make area select think it should be doing something.
        let locMouse = _mouseDown;

        // Process area selection if it was a quick drag - mouse up and nothing has been processed yet
        let context = this.curContext();
        if (wasDragging && !_dimPosChanged) {
            if (!acceptedCandidates && context) {
                delaySelectObjectsInArea.cancel();
                await selectObjectsInArea(context, locMouse, e);
            }
        }

        let locIgnoreMouseUp = this.ignoreMouseUp || _dimPosChanged;
        _mouseDown = null;
        _dimPosChanged = false;
        this.ignoreMouseUp = undefined;

        clearMarkersOfRecType();

        // If the mouse down is not in the graphics area, we should not process mouse up for selection/deselection
        if (!_mouseDownInGraphicArea) {
            _clickedEmptySpace = false;
            return;
        }
        _mouseDownInGraphicArea = false;

        // Return when:
        //  1. don't process mouse up event if UI overrides the event
        //  2. If we think camera is currently being changed
        //  3a. if we never got mouseDown event (because sgo swallowed it) TEMP PLMVisWeb eats mouse downs for Dimension and PMI,
        //                                                                skip until we get some resolution on this.
        //  3b. if someone explicitly told us to ignore mouse up (temp, since we can't depend on mouse down)
        //  4. is not MB1
        //  5. is not canvas
        // <Lingyun> The previous fix caused a regression - after rotation by MB2 or pan by MB3, there is not selection
        // pre-highlight. This is because _dragging is not reset. One needs to click anywhere and preceed which
        // also caused the selected objects being erased. Move _dragging = false ahead to fix the regression
        // <Lingyun 05/19/2021> Allow MB3 selection per NSAF-1869
        if (
            _override ||
            _cameraChange ||
            /*  !locMouse || */
            locIgnoreMouseUp ||
            (e.button !== 0 && e.button !== 2) ||
            !viewer.input.overCanvas(e)
        ) {
            return;
        }

        if (locMouse) {
            let dist = Math.sqrt(Math.pow(e.offsetX - locMouse.x, 2) + Math.pow(e.offsetY - locMouse.y, 2));
            if (dist > 5 && context && !context.getAllowAreaSelect()) {
                //drag, not click
                return;
            }
        }

        // Starting from PLMVisWeb 1.7.4-31, PLMVisWeb will not stops the propagation of the event in the inputManager.
        // AtlasSelection needs to stop the event propagation in all its listeners after the event is handled.
        e.stopPropagation();

        if (context) {
            if (acceptedCandidates && acceptedCandidates.validated) {
                _clickedEmptySpace = false;
                if ((acceptedCandidates.autoDesel && !acceptedCandidates.deselection) || (exclusiveGlobalSelection && !e.ctrlKey && !e.shiftKey)) {
                    deselectDisplay(context, SELECTION_REASONS.TERMINATE);
                    context.clearSelected();
                }
                if (e.button === 0 || (e.button === 2 && _mb3WithSelection)) {
                    // <05/19/2021> Per NSAF-1869, we make the selection only when MB1 is pressed or MB3 is pressed
                    // and _mb3WithSelection is set to true from galaxy-selection (global selection)
                    // No selection is made when MB3 is pressed and _mb3WithSelection === false (selection from command)
                    highlightCandidates(acceptedCandidates, false, true);
                    this.updateSelected(acceptedCandidates.validated.addCandidates, acceptedCandidates.validated.removeCandidates, true);
                } else if (e.button === 2 && !_mb3WithSelection) {
                    // This happens when using MB3 to selection in a command (not in global selection)
                    // In this case, we don't want to actually select an object. Instead, we want to bring up PickThru.
                    // The problem is "deselectDisplay(context);" above also dehighlight the object picked by MB3.
                    // Because of this, we need to highlight the first object again here.
                    if (acceptedCandidates && acceptedCandidates.candidateSet) {
                        let candidates = Array.from(acceptedCandidates.candidateSet);
                        if (candidates.length > 0) {
                            let setForFirst = new Set([candidates[0]]);
                            acceptedCandidates.candidateSet = setForFirst;
                            highlightCandidates(acceptedCandidates, true, false);
                        }
                    }
                }

                //we've used the candidates, so clear them
                acceptedCandidates = null;
            } else if (wasDragging) {
                // If user was dragging and just released mouse while acceptedCandidates is empty, just return quietly, no need to update selection list.
                return;
            } else {
                // candidate is empty but user may click without moving mouse, validate objects under cursor directly
                const params = { visibleOnly: false };
                let curObjects = viewer.getObjectIdsAtViewCoordinate(e.offsetX, e.offsetY, params);
                curObjects = filterOutObjectsInHiddenBody(curObjects);
                if (curObjects && curObjects.length > 0) {
                    _clickedEmptySpace = false;
                    if (e.shiftKey && !e.ctrlKey && (e.button === 0 || (e.button === 2 && _mb3WithSelection))) {
                        // Deselect the one under cursor
                        let selected = Array.from(context.getSelected());
                        let deSel = findSelectedNearPickPoint(curObjects, selected);
                        if (deSel.length > 0) {
                            this.updateSelected([], deSel, true);
                            return;
                        }
                    }
                    // Nothing can be deselected, check if anything can be selected
                    if (
                        ((!exclusiveGlobalSelection && !e.shiftKey) || (exclusiveGlobalSelection && (e.shiftKey || e.ctrlKey))) &&
                        (e.button === 0 || (e.button === 2 && _mb3WithSelection))
                    ) {
                        await validateObjectsUnderCursor(context, e, false);
                        if (acceptedCandidates) {
                            highlightCandidates(acceptedCandidates, false, true);
                            this.updateSelected(acceptedCandidates.validated.addCandidates, acceptedCandidates.validated.removeCandidates, true);
                        }
                        acceptedCandidates = null;
                    }
                } else {
                    //if something was selected, need to dispatch deselection
                    if (exclusiveGlobalSelection && e.shiftKey) {
                        return;
                    }
                    _clickedEmptySpace = true;
                    if (e.button === 0) {
                        // Deselect everything only when MB1 click in the empty area
                        // CAM had a case where objects are selected, they want to use MB3 to bring up
                        // the context menu. Without this fix, the objects are deselected when using MB3
                        // to bring up the context menu.
                        // NX also only deselects using MB1 in the empty area
                        if (!_deselectAll) {
                            /**
                             * setDelayedDeselectAll allows us to skip 1 deselectAll cycle.
                             * This is important because after we restore the mb3 selection data
                             * from context toolbar, if we click outside, deselectAll will be forced.
                             * We want to prevent just 1 cycle. This should not affect other clients (uielements
                             * that aren't context toolbar)).
                             */
                            _deselectAll = true;
                        } else {
                            this.deselectAll(context);
                        }
                    }
                }
            }
        }
    };

    const _keyDownCb = (event) => {
        if (event.code === 'Escape' && !_escKeepSelected) {
            this.deselectAll(this.curContext());
        }
    };

    let _dimPosChanged = false;
    const _dimPositionChangedCb = () => {
        _dimPosChanged = true;
    };

    // the '0' makes this a high priority.  This is needed for _override,
    // because Galaxy is unsetting _override in their mouse up cb.  If
    // their cb gets called before ours, we'll process the mouse up since
    // _override is unset by that point.
    viewer.input.addEventListener(PLMVisWeb.InputEventTypes.Up, _mouseUpCb, 0);
    // pvw did an event refactor for handling mouse event. The viewer did an event.override = true in dimension dragging which
    // prevented the mouse down event getting dispatched any further. This caused the failure in selecting dimensions.
    // Registering the mouse down as priority 1 makes the current _mouseDownCb to have higher priority than the viewer in
    // receiving mouse down event. Therefore, _mouseDownCb gets the event first. We should not override the event.
    // Otherwise, viewer will not get the event.
    viewer.input.addEventListener(PLMVisWeb.InputEventTypes.Down, _mouseDownCb, 1);
    viewer.input.addEventListener(PLMVisWeb.InputEventTypes.Move, _mouseMoveCb);

    // Register dragover event here to evaluate the current selection context in SWF drag and drop scenario.
    // In SWF, the drag and drop handlers are registered at canvas's ancestor node.
    // In order to run drag over logic before SWF handler,
    // we need to add third parameter to tunnel rather than bubble the event.
    viewer.domElement.ownerDocument.addEventListener('dragover', _dragOverCb, {
        capture: true
    });

    document.body.addEventListener('keydown', _keyDownCb, { capture: true });
    if (viewer.dimension) {
        viewer.dimension.addEventListener(PLMVisWeb.DimensionEventTypes.PositionChanged, _dimPositionChangedCb);
    }
    /**
     *
     * @param {boolean} [addHidden]
     * @returns {Promise<SelectionObject[]>}
     */
    this.findAllAcceptedCandidates = async (addHidden = true) => {
        let results = [];
        if (acceptedCandidates && acceptedCandidates.candidateSet) {
            let candidateArray = [];
            for (let cand of acceptedCandidates.candidateSet) {
                if (cand.isBlocked && !addHidden) {
                    // this and all further candidates will be invalid, so jump to end
                    break;
                }
                candidateArray.push(cand);
            }
            let validatedCands = await validateCandidate(candidateArray, acceptedCandidates.deselection, acceptedCandidates.autoDesel).then((res) =>
                res.accepted
                    ? {
                          addCandidates: res.addCandidates,
                          removeCandidates: res.removeCandidates
                      }
                    : undefined
            );
            if (validatedCands) {
                if (acceptedCandidates.deselection) {
                    if (validatedCands.removeCandidates) {
                        results = validatedCands.removeCandidates;
                    }
                } else if (validatedCands.addCandidates) {
                    results = validatedCands.addCandidates;
                }
            }
        }
        return results;
    };

    this.getCurrentCandidates = () => {
        if (acceptedCandidates) {
            return acceptedCandidates.validated;
        }
    };

    this.pushContext = (context) => {
        // if( contextStack.length > 0 )
        // ; //unhighlight current context
        contextStack.push(context);
        //highlight new context
    };
    this.popContext = () => {
        if (contextStack.length < 1) {
            return;
        }
        let curContext = this.curContext();
        // let all elements know this context is losing focus,
        // in case they've drawn something on screen (snap point)
        curContext.onPop();

        //unhighlight current context
        contextStack.pop();
        //if( contextStack.length > 0 )
        //; //highlight new context
    };
    /**
     * @returns {Context}
     */
    this.curContext = () => {
        if (contextStack.length > 0) {
            return contextStack[contextStack.length - 1];
        }
        return null;
    };
    /**
     * @param {SelectionCallback} listener
     */
    this.addSelectionListener = (listener) => {
        emitter.addEventListener(SELECTION_EVENT_TYPE.SELECTION, listener);
    };
    /**
     * @param {SelectionCallback} listener
     */
    this.addPreselectionListener = (listener) => {
        emitter.addEventListener(SELECTION_EVENT_TYPE.PRESELECTION, listener);
    };
    /**
     * @param {SelectionCallback} listener
     */
    this.removeSelectionListener = (listener) => {
        emitter.removeEventListener(SELECTION_EVENT_TYPE.SELECTION, listener);
    };
    /**
     * @param {SelectionCallback} listener
     */
    this.removePreselectionListener = (listener) => {
        emitter.removeEventListener(SELECTION_EVENT_TYPE.PRESELECTION, listener);
    };
    /**
     * Highlighting may be delayed for a while after user hover on geometry!
     *
     * @param {HighlightCallback} listener
     */
    this.addHighlightListener = (listener) => {
        emitter.addEventListener(SELECTION_EVENT_TYPE.HIGHLIGHT, listener);
    };
    /**
     * @param {HighlightCallback} listener
     */
    this.removeHighlightListener = (listener) => {
        emitter.removeEventListener(SELECTION_EVENT_TYPE.HIGHLIGHT, listener);
    };
    this.setMouseEventOverride = (override) => {
        _override = override;
    };
    let _mb3WithSelection = undefined; // This variable is meaningful only when mouse button MB3 is pressed
    this.setMB3WithSelection = (mb3WithSelection) => {
        _mb3WithSelection = mb3WithSelection;
    };

    let _deselectAll = true;
    this.setDelayedDeselectAll = () => {
        _deselectAll = false;
    };
    let _clickedEmptySpace = false;
    /**
     * This allows us to know if we selected something in the 3d graphics viewer
     * or if we selected empty space. This is crucial for the context toolbar
     * to work around this issue:
     * 
     * It always pops up and then populates itself based on current selection. This makes it more flexible
     * but we have to be able to hide it at will via empty space.
     * 
     * @returns {boolean} _clickedEmptySpace
     */
    this.getClickedEmptySpace = () => {
        return _clickedEmptySpace;
    };

    this.setSecondarySelectionMaterial = (secondarySelectionMatDef) => {
        setSecondarySelectionMaterial(secondarySelectionMatDef);
    };

    /**
     *  validateSelectionContext checks the input selContext.
     *  toAdd === true: selContext is to be added to the selection context;
     *  toAdd === false: selContext is to be removed from the selection context.
     *  The following are the possible errors for the input selection context:
     *      1) "Input selection context is valid."
     *      2) "The input object to remove is not an instance of selection context."
     *      3) "The input selection context cannot be removed since it is not the current selection context."
     *      4) "The input selection context cannot be removed since it is the global selection context."
     *      5) "The selection context stack doesn't contain the input selection context to remove."
     *      6) "The input object to add is not an instance of selection context."
     *      7) "The input selection context to add is already in the context stack."
     *
     * @param {Context} selContext
     * @param {boolean} toAdd
     */
    this.validateSelectionContext = (selContext, toAdd) => {
        // check for remove selContext from contextStack
        if (!toAdd) {
            if (selContext) {
                // Minimum check on removeSelectionContext
                if (!(selContext instanceof Context)) {
                    throw new Error('The input object to remove is not an instance of selection context.');
                }

                if (this.curContext() !== selContext) {
                    throw new Error('The input selection context cannot be removed since it is not the current selection context.');
                }

                // check if the selection stack contains selContext
                let found = false;
                for (const contx of contextStack) {
                    if (contx === selContext) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    throw new Error('The selection context stack does not contain the input selection context to remove.');
                }
            }
        }

        // check for add selContext to contextStack
        if (toAdd) {
            if (selContext) {
                // Minimum check on selContext
                if (!(selContext instanceof Context)) {
                    throw new Error('The input object to add is not an instance of selection context.');
                }

                // check if the selContext is already in contextStack
                let found = false;
                for (const contx of contextStack) {
                    if (contx === selContext) {
                        found = true;
                        break;
                    }
                }
                if (found) {
                    throw new Error('The input selection context to add is already in the context stack.');
                }
            }
        }
    };

    const processDimensionDrag = (viewer, selContext) => {
        let draggable = false;
        if (selContext.hasActiveElement('Dimension')) {
            draggable = true;
        }
        viewer.dimension.setDimensionDragFlag(draggable);
    };

    /**
     * replaceSelectionContexts manages the selection contexts in Atlas Selection Context Stack (ASSCS) and removes/adds the
     * selection contexts
     * This function first checks the validity of the input. The right combinations are:
     *     i) removeSelectionContext and addSelectionContext are both valid
     *     ii) removeSelectionContext === null (or undefined) and addSelectionContext is valid
     *     iii) removeSelectionContext is valid and addSelectionContext  === null (or undefined)
     * For any valid "removeSelectionContext", it:
     *     a) gets the selected objects in the removeSelectionContext, change their selection color;
     *     b) pops removeSelectionContext from ASSCS;
     *     c) If removeSelectionContext === null, skip the above steps
     *     d) if addSelectionContext === null, it gets the next selection context in ASSCS and does steps e) and f).
     * For the valid addSelectionContext, it:
     *     e) gets the selection elements/filters from addSelectionContext or the next context in stack and install them.
     *     f) gets the selected objects from addSelectionContext or the next context in stack and color them in the selection color
     *
     * @param {Context} removeSelectionContext - the selection context to remove from ASSCS
     * @param {Context} addSelectionContext - the selection context to add to ASSCS.
     */
    this.replaceSelectionContexts = (removeSelectionContext, addSelectionContext) => {
        try {
            this.validateSelectionContext(removeSelectionContext, false);
        } catch (err) {
            console.error(err);
            return;
        }

        try {
            this.validateSelectionContext(addSelectionContext, true);
        } catch (err) {
            console.error(err);
            return;
        }

        // removeSelectionContext and addSelectionContext cannot be both null or undefined
        if (!removeSelectionContext && !addSelectionContext) {
            console.error('The selection contexts to add and remove are both null or undefined.');
            return;
        }

        // remove selection context
        if (removeSelectionContext) {
            if (contextStack.length > 1) {
                // don't allow to pop the last selection context
                if (removeSelectionContext.getSecondaryActive()) {
                    addToContextList(viewer, removeSelectionContext, false);
                } else {
                    displaySelectedInContext(
                        viewer,
                        removeSelectionContext,
                        PLMVisWeb,
                        removeSelectionContext.getSelected(),
                        SELECTION_REASONS.TERMINATE,
                        false
                    );
                }
                this.popContext();
            }
            if (contextStack.length === 1 && !addSelectionContext) {
                // Restore the selection display of the last context in contextStack - this is the global
                // selection context
                displaySelectedInContext(viewer, contextStack[0], PLMVisWeb, contextStack[0].getSelected(), SELECTION_REASONS.FOCUS_IN, true);
            }
            // When removing a selection context, the dimension drag flag is reset to false due to the
            // removal of the selection context. After removeSelectionContext is popped from the context stack,
            // the previous selection context comes on top of the selection stack. The problem is that the dimensions
            // in this selection context are not draggeable even if this selection context has the active dimension
            // element. This is because the dimension drag flag is already set to false. We need to check if
            // this selection context has the dimension element active and if it has we need to make sure
            // the dimension drag flag is set to true.
            processDimensionDrag(viewer, contextStack[contextStack.length - 1]);
        }

        // add selection context.
        if (addSelectionContext) {
            if (contextStack.length === 1) {
                // Deselect everything in the first context (global selection context) if a new selection
                // context is added
                deselectDisplay(contextStack[0], SELECTION_REASONS.TERMINATE);
            }
            addToContextList(viewer, addSelectionContext, true);
            setPrimarySelectionColor(viewer, PLMVisWeb, addSelectionContext);
            this.pushContext(addSelectionContext);
            // set dimension draggability based on if the dimension element is active in this selection context.
            processDimensionDrag(viewer, addSelectionContext);
        }
    };

    /**
     *  Display psIds using the specified material defined in matDef
     *
     *  @param {Array<string>} psIds - psIds to be displayed using the specified material definition
     *  @param {object} matDef - material definition to display all the psIds. If null or undefined, resetMaterialByPsId is called
     */
    this.setMaterialByPsIds = (psIds, matDef) => {
        setMaterialByPsIds(viewer, psIds, matDef);
    };

    /**
     *  Given an array of the selection contexts, this function filters the inactive selection contexts from the current selection context.
     *  Then, this function gets the selected objects and the objects in their psIds from the inactive select contexts excluding the selected
     *  objects and objects in their psId arrays in the current selection context.
     *
     * @param {Iterable<Context>} selectionContexts
     * @returns {{ selectedObjects: Set<any>, selectedObjectPsIds: Set<any>}}
     */
    this.getSelectedObjectsInactiveContexts = (selectionContexts) => {
        let curContext = this.curContext();
        let inactiveContextSet = new Set();
        for (const context of selectionContexts) {
            if (context !== curContext) {
                inactiveContextSet.add(context);
            }
        }
        let selObjs = getSelectedObjectsInactiveContexts(curContext, Array.from(inactiveContextSet));
        return selObjs;
    };

    /**
     *  resets the point cache
     */
    this.resetPointCache = () => {
        resetPointCache();
    };

    /**
     * Sets whether to keep the selected objects when ESC key is pressed
     *
     * @param {boolean} escKeepSelected - true to keep the selected objects and false to discard the selected objects
     */
    this.setEscKeepSelected = (escKeepSelected) => {
        _escKeepSelected = escKeepSelected;
    };
}

export { AtlasSelection, SELECTION_COLORS };
export default AtlasSelection;
